import { useCallback, useEffect, useState } from "react";
import apiApplicationService from "../../../service/apiApplicationService";
import { ExternalResource } from "./types";

/**
 * Session-level cache for GET /api/api-applications/resources/. The catalog is
 * static per deployment, so the list page preloads it and the form reuses the
 * same promise; `retry` refetches after a failure.
 */
let cached: Promise<ExternalResource[]> | null = null;

export const preloadExternalResources = () => {
  if (!cached) {
    cached = apiApplicationService.getResources().catch((err) => {
      cached = null;
      throw err;
    });
  }
  return cached;
};

export function useExternalResources() {
  const [resources, setResources] = useState<ExternalResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    preloadExternalResources()
      .then((data) => {
        if (!cancelled) setResources(data);
      })
      .catch(() => {
        if (!cancelled) setError("Could not load the external API catalog.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(load, [load]);

  return { resources, loading, error, retry: load };
}
